// @ts-nocheck
import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  Button, Drawer, Input, Select, Switch, message, Dropdown, Avatar,
} from "../components/admin-ui";
import type { MenuProps, ColumnsType } from "../components/admin-ui";
import {
  MoreOutlined, SearchOutlined, TeamOutlined, CheckCircleOutlined,
  CloseCircleOutlined, KeyOutlined, UserOutlined, EnvironmentOutlined,
  ShoppingOutlined,
} from "../components/admin-icons";
import { DataTable } from "../components/DataTable";
import { StatusBadge } from "../components/StatusBadge";
import { AdminShell } from "../layouts/AdminShell";
import { tokens } from "../styles/tokens";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { formatDateOnly } from "../utils/formatLocaleDate";
import { getInitials, getAvatarColor } from "../utils/staffDisplay";
import {
  useAdminCustomersList, useAdminCustomer, useUpdateCustomer, useResetCustomerPassword,
  type AdminCustomerListItem,
} from "../api/hooks";
const { color: T } = tokens;

/* ─── Stat card (mirrors StaffStatCard) ───────────────────── */
function CustomerStatCard({ icon, label, value, tone }: {
  icon: React.ReactNode; label: string; value: number; tone: "total" | "active" | "inactive";
}) {
  return (
    <div className={`admin-kpi-card admin-staff-stat admin-staff-stat-${tone}`}>
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon">{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
    </div>
  );
}

/* ─── Detail Drawer ────────────────────────────────────────── */
function CustomerDetailDrawer({ customerId, open, onClose }: {
  customerId: string | null; open: boolean; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { confirm: confirmDialog } = useConfirmDialog();
  const { data, isLoading } = useAdminCustomer(customerId ?? undefined);
  const customer = data?.data;
  const updateCustomer = useUpdateCustomer();
  const resetPassword = useResetCustomerPassword();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const startEdit = () => {
    if (!customer) return;
    setName(customer.name ?? "");
    setEmail(customer.email ?? "");
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!customer) return;
    try {
      await updateCustomer.mutateAsync({ id: customer.id, name, email });
      void message.success(t("customers.saveSuccess"));
      setEditing(false);
    } catch {
      void message.error(t("customers.saveFailed"));
    }
  };

  const toggleActive = async (checked: boolean) => {
    if (!customer) return;
    if (!checked) {
      const ok = await confirmDialog({
        title: t("customers.disableConfirmTitle"),
        description: t("customers.disableConfirmDesc"),
        confirmLabel: t("customers.disableAction"),
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await updateCustomer.mutateAsync({ id: customer.id, is_active: checked });
      void message.success(t("customers.statusUpdateSuccess"));
    } catch {
      void message.error(t("customers.statusUpdateFailed"));
    }
  };

  const doResetPassword = async () => {
    if (!customer) return;
    const ok = await confirmDialog({
      title: t("customers.resetPasswordConfirmTitle"),
      description: t("customers.resetPasswordConfirmDesc"),
      confirmLabel: t("customers.resetPassword"),
      danger: true,
    });
    if (!ok) return;
    try {
      const res = await resetPassword.mutateAsync(customer.id);
      // The temp password is only ever returned this once — show it in a
      // second dialog (not a toast, which auto-dismisses too fast to copy).
      void confirmDialog({
        title: t("customers.tempPasswordTitle"),
        description: t("customers.tempPasswordDesc", { password: res.data.tempPassword }),
        confirmLabel: t("customers.tempPasswordCopied"),
        cancelLabel: t("customers.close"),
      });
    } catch {
      void message.error(t("customers.resetPasswordFailed"));
    }
  };

  const handleClose = () => { setEditing(false); onClose(); };

  return (
    <Drawer
      open={open} onClose={handleClose} placement="right" width={480}
      title={<span style={{ fontSize: 18, fontWeight: 900, color: T.textDark }}>{t("customers.detailTitle")}</span>}
      afterOpenChange={(v) => { if (!v) setEditing(false); }}
    >
      <div style={{ padding: "20px 20px 100px" }}>
      {!isLoading && customer && (
        <>
          {/* Profile */}
          <div style={{ background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <Avatar size={44} style={{ background: getAvatarColor(customer.name ?? customer.phone), fontSize: 15, fontWeight: 900, flexShrink: 0 }}>
                {getInitials(customer.name ?? customer.phone)}
              </Avatar>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800, fontSize: 16, color: T.textDark }}>{customer.name ?? "—"}</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>{customer.phone}</div>
              </div>
              <StatusBadge status={customer.is_active ? "active" : "inactive"} kind="staff"
                label={customer.is_active ? t("customers.statusActive") : t("customers.statusInactive")} />
            </div>

            {editing ? (
              <div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, display: "block", marginBottom: 4 }}>{t("customers.fullName")}</label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} style={{ height: 40, borderRadius: 10 }} />
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 11, fontWeight: 800, color: T.textMuted, display: "block", marginBottom: 4 }}>{t("customers.email")}</label>
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} style={{ height: 40, borderRadius: 10 }} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button onClick={() => setEditing(false)} style={{ flex: 1, height: 40, borderRadius: 10, fontWeight: 600 }}>{t("customers.close")}</Button>
                  <Button type="primary" loading={updateCustomer.isPending} onClick={saveEdit}
                    style={{ flex: 1, height: 40, borderRadius: 10, fontWeight: 700, background: T.brand, borderColor: T.brand }}>
                    {t("customers.save")}
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
                  <span style={{ color: T.textMuted }}>{t("customers.email")}</span>
                  <span style={{ color: T.textDark, fontWeight: 600 }}>{customer.email ?? "—"}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
                  <span style={{ color: T.textMuted }}>{t("customers.registeredVia")}</span>
                  <span style={{ color: T.textDark, fontWeight: 600 }}>{customer.registered_via}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 12 }}>
                  <span style={{ color: T.textMuted }}>{t("customers.registeredOn")}</span>
                  <span style={{ color: T.textDark, fontWeight: 600 }}>{formatDateOnly(customer.created_at, i18n.language)}</span>
                </div>
                <Button icon={<UserOutlined />} onClick={startEdit} style={{ height: 38, borderRadius: 10, fontWeight: 600, width: "100%" }}>
                  {t("staff.edit")}
                </Button>
              </div>
            )}
          </div>

          {/* Account status + reset password */}
          <div style={{ background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{t("customers.accountStatus")}</span>
              <Switch checked={customer.is_active} onChange={toggleActive} disabled={updateCustomer.isPending} />
            </div>
            <Button icon={<KeyOutlined />} onClick={doResetPassword} loading={resetPassword.isPending}
              danger style={{ height: 40, borderRadius: 10, fontWeight: 700, width: "100%" }}>
              {t("customers.resetPassword")}
            </Button>
          </div>

          {/* Addresses */}
          <div style={{ background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <EnvironmentOutlined style={{ color: T.brand }} />
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: T.textDark }}>{t("customers.addresses")}</span>
            </div>
            {customer.addresses.length === 0 ? (
              <div style={{ fontSize: 13, color: T.textMuted }}>{t("customers.noAddresses")}</div>
            ) : (
              customer.addresses.map((a) => (
                <div key={a.id} style={{ padding: "10px 0", borderBottom: "1px solid #F3F4F6" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, color: T.textDark }}>
                    {a.label}
                    {a.is_default && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.brand, background: T.brandLight, borderRadius: 100, padding: "1px 8px" }}>
                        {t("customers.defaultAddress")}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted }}>{a.address}</div>
                  {a.contact && <div style={{ fontSize: 12, color: T.textMuted }}>{a.contact}</div>}
                </div>
              ))
            )}
          </div>

          {/* Recent Orders */}
          <div style={{ background: T.surface, borderRadius: 14, border: "1px solid #F3F4F6", padding: "18px 20px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              <ShoppingOutlined style={{ color: T.brand }} />
              <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: T.textDark }}>{t("customers.recentOrders")}</span>
            </div>
            {customer.orders.length === 0 ? (
              <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 12 }}>{t("customers.noOrders")}</div>
            ) : (
              customer.orders.map((o) => (
                <div key={o.id}
                  onClick={() => navigate(`/orders/${o.id}`)}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #F3F4F6", fontSize: 13, cursor: "pointer" }}
                >
                  <span style={{ fontWeight: 600, color: T.brand, textDecoration: "underline" }}>#{o.order_code}</span>
                  <StatusBadge status={o.status} kind="order" label={o.status} />
                  <span style={{ color: T.textMuted }}>{formatDateOnly(o.created_at, i18n.language)}</span>
                </div>
              ))
            )}
            <Button onClick={() => navigate(`/orders?customerId=${customer.id}`)}
              style={{ height: 38, borderRadius: 10, fontWeight: 700, width: "100%", marginTop: 12 }}>
              {t("customers.viewAllOrders")}
            </Button>
          </div>
        </>
      )}
      </div>
    </Drawer>
  );
}

/* ─── Screen ─────────────────────────────────────────────── */
type StatusFilter = "all" | "active" | "inactive";

export function CustomersScreen() {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;
  // Search, status filter and paging are all done by the server — the stat cards must count every customer, not one page.
  const { data, isLoading } = useAdminCustomersList({
    q: search.trim() || undefined,
    isActive: statusFilter === "all" ? undefined : statusFilter === "active",
    page, limit: PAGE_SIZE,
  });
  const filtered = data?.data.items ?? [];
  const totalCustomers = data?.data.counts.all ?? 0;
  const activeCount = data?.data.counts.active ?? 0;
  const inactiveCount = data?.data.counts.inactive ?? 0;

  const openDetail = (c: AdminCustomerListItem) => { setDetailId(c.id); setDetailOpen(true); };

  const columns: ColumnsType<AdminCustomerListItem> = [
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("customers.colName")}</span>,
      key: "name", width: 220,
      render: (_, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar size={36} style={{ background: getAvatarColor(row.name ?? row.phone), fontSize: 12, fontWeight: 900, flexShrink: 0 }}>
            {getInitials(row.name ?? row.phone)}
          </Avatar>
          <span style={{ fontWeight: 700, color: T.textDark, fontSize: 14 }}>{row.name ?? "—"}</span>
        </div>
      ),
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("customers.colContact")}</span>,
      key: "contact", width: 200,
      render: (_, row) => (
        <div>
          <div style={{ fontSize: 13, color: T.textDark }}>{row.phone}</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>{row.email ?? "—"}</div>
        </div>
      ),
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("customers.colStatus")}</span>,
      dataIndex: "is_active", key: "status", width: 110,
      render: (v: boolean) => (
        <StatusBadge status={v ? "active" : "inactive"} kind="staff" label={v ? t("customers.statusActive") : t("customers.statusInactive")} />
      ),
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("customers.colOrders")}</span>,
      dataIndex: "order_count", key: "orders", width: 90,
      render: (v: number) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("customers.colRegistered")}</span>,
      dataIndex: "created_at", key: "created", width: 120,
      render: (v: string) => <span style={{ fontSize: 13, color: T.textBody }}>{formatDateOnly(v, i18n.language)}</span>,
    },
    {
      title: <span style={{ fontSize: 11, fontWeight: 800, color: T.textMuted }}>{t("customers.colActions")}</span>,
      key: "actions", width: 70, align: "center",
      render: (_, row) => {
        const items: MenuProps["items"] = [
          { key: "view", label: <span style={{ fontWeight: 600 }}>{t("customers.viewDetail")}</span>, onClick: () => openDetail(row) },
        ];
        return (
          <Dropdown menu={{ items }} trigger={["click"]} placement="bottomRight">
            <Button aria-label={t("customers.colActions")} type="text" icon={<MoreOutlined style={{ fontSize: 18 }} />} />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <AdminShell>
      <div className="admin-staff-page admin-list-page">
        <div className="admin-staff-page-inner">
          <div className="admin-staff-toolbar">
            <div>
              <h1 className="admin-page-title">{t("customers.managementTitle")}</h1>
              <p className="admin-staff-subtitle">{t("customers.pageSubtitle")}</p>
            </div>
          </div>

          <div className="admin-staff-stats admin-txn-stats grid">
            <CustomerStatCard icon={<TeamOutlined />} label={t("customers.totalCustomers")} value={totalCustomers} tone="total" />
            <CustomerStatCard icon={<CheckCircleOutlined />} label={t("customers.enabledCount")} value={activeCount} tone="active" />
            <CustomerStatCard icon={<CloseCircleOutlined />} label={t("customers.disabledCount")} value={inactiveCount} tone="inactive" />
          </div>

          <div className="admin-staff-filters admin-txn-filter-panel">
            <Input className="admin-staff-search" prefix={<SearchOutlined style={{ color: T.textMuted }} />}
              placeholder={t("customers.searchPlaceholder")} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} allowClear />
            <Select value={statusFilter} onChange={(v) => { setStatusFilter(v as StatusFilter); setPage(1); }}
              options={[
                { value: "all", label: t("customers.allStatus") },
                { value: "active", label: t("customers.activeStatus") },
                { value: "inactive", label: t("customers.inactiveStatus") },
              ]} />
          </div>

          <p className="admin-staff-results">{t("common.total", { count: data?.data.total ?? 0 })}</p>
          <div className="admin-staff-table-panel admin-data-table-panel">
            <DataTable<AdminCustomerListItem>
              columns={columns} dataSource={filtered} loading={isLoading} rowKey="id" sticky={false}
              pagination={{ current: page, total: data?.data.total ?? 0, pageSize: PAGE_SIZE, showSizeChanger: false, onChange: (p: number) => setPage(p) }}
              onRow={(row) => ({ onClick: () => openDetail(row), style: { cursor: "pointer" } })}
            />
          </div>

          <CustomerDetailDrawer customerId={detailId} open={detailOpen} onClose={() => setDetailOpen(false)} />
        </div>
      </div>
    </AdminShell>
  );
}
